import { duplicateAudioAsset } from '../components/gridpagefunction/Audio';
import { duplicateImageAsset } from '../components/gridpagefunction/image';
import { duplicateMeshAsset } from '../components/gridpagefunction/Mesh';
import { findMeshItemForSceneObject } from '../components/GridAssets/shapeAssetUtils';
import { duplicateMeshObjAsset } from '../components/gridpagefunction/MeshObj';
import { handleAddText } from '../components/gridpagefunction/Text';
import { duplicateVideoAsset } from '../components/gridpagefunction/Video';
import { duplicateHotspot } from '../components/gridpagefunction/HotSpot';
import { duplicateParticleAsset } from './particles/HandleDuplicatePartical';
import { pasteItem, findItem } from '../components/Copy-Paste';
import { updateCameraPreview, selectObjectInScene } from '../pages/Dashboard/GridPage';
import {
  lightDataUpdate,
  updateCameraType,
  updateOpenModals,
} from '../redux/Slice/gridSlice';
import store from '../redux/store';
import { updateActiveComponent } from '../redux/Slice/globalStates';
import toast from 'react-hot-toast';
import { syncViewportCameraSwitch } from './ThreeJsUtils/viewportCameraSync';

// Regular expressions for validating different input fields
export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Basic email format validation
export const mobileRegex = /^[0-9]{10}$/; // Ensures exactly 10 digits for mobile numbers
export const pincodeRegex = /^[0-9]{6}$/; // Ensures exactly 6 digits for pin codes (modify for other formats if needed)
export const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,15}$/;
// Ensures password is 8-15 characters long and contains at least:
// - One uppercase letter
// - One lowercase letter
// - One number
// - One special character

/**
 * Function to validate form data based on the specified fields.
 * @param {Object} data - The object containing field values.
 * @param {Array} fields - The list of fields to validate.
 * @returns {Object} - An object containing error messages for invalid fields.
 */
export const validateFunction = (data, fields) => {
  const errors = {}; // Object to store validation errors

  fields.forEach((field) => {
    const value = data[field];

    // Check if the value is empty or undefined
    if (value === undefined || value === null || String(value).trim() === '') {
      errors[`${field}_err`] = `${field.replace('_', ' ')} is required`;
    } else {
      switch (field) {
        case 'email':
          if (!emailRegex.test(value)) {
            errors[`${field}_err`] = 'Invalid email address';
          }
          break;
        case 'mobile':
          if (!mobileRegex.test(value)) {
            errors[`${field}_err`] =
              'Invalid mobile number (must be 10 digits)';
          }
          break;
        case 'pincode':
          if (!pincodeRegex.test(value)) {
            errors[`${field}_err`] = 'Invalid pincode (must be 6 digits)';
          }
          break;
        case 'password':
          if (!passwordRegex.test(value)) {
            errors[`${field}_err`] =
              'Password must be 8-15 characters long, with at least 1 uppercase, 1 lowercase, 1 number, and 1 special character.';
          }
          break;
        default:
          break; // No validation required for unlisted fields
      }
    }
  });

  return errors; // Return the object containing error messages
};

/**
 * Function to handle unauthorized access responses.
 * @param {Object} response - The API response object.
 */
export const UnauthorizedFunction = (response) => {
  if (response?.message == 'Unauthorized') {
    // Shared preview links are public — never bounce them to login.
    const path = window.location?.pathname || '';
    if (path.startsWith('/preview-page/')) {
      return;
    }

    localStorage.clear(); // Clear stored session data
    // alert('Unauthorized access detected. Please log in again.');
    window.location.href = `/`;
    // window.location.href = 'https://devgrid.spatialgrid.ai/login'; // Redirect to login page
    window.reload(); // Reload the page to enforce logout
  }
};

export const toDegrees = (rad) =>
  (parseFloat(rad) * (180 / Math.PI)).toFixed(2);

export const generateThumbnail = async (videoUrl) => {
  return await new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.src = videoUrl;
    video.crossOrigin = 'anonymous'; // Important if loading from external domain
    video.muted = true;

    video.addEventListener('loadeddata', () => {
      // Wait until video metadata is loaded
      video.currentTime = Math.min(0.5, video.duration / 2);
    });

    video.addEventListener('seeked', () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const thumbnailDataUrl = canvas.toDataURL('image/png');
      resolve(thumbnailDataUrl);
    });

    video.addEventListener('error', (err) => {
      reject(err);
    });
  });
};
// Calculate contrast color (black or white) based on background luminance
export const getContrastColor = (hex) => {
  // Remove # and convert hex to RGB
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  // Calculate luminance (simplified YIQ formula)
  const luminance = (r * 299 + g * 587 + b * 114) / 1000;
  // Return white for dark colors, black for light colors
  return luminance > 128 ? '#000000' : '#FFFFFF';
};

export const switchCamera = (object, value) => {
  const subValue = value === 'orthographic';
  const fromCamera = object.currentCamera;
  const newCamera = subValue
    ? object.orthographicCamera
    : object.perspectiveCamera;

  if (!fromCamera || !newCamera || !object.controlsOrbit) return;

  syncViewportCameraSwitch(
    fromCamera,
    newCamera,
    object.controlsOrbit.target,
    window.innerWidth,
    window.innerHeight
  );

  object.controlsOrbit.object = newCamera;
  object.controlsOrbit.update();

  object?.renderer?.render(object.scene, newCamera);
  object.update(newCamera);
  store.dispatch(updateCameraType(value));
  localStorage.setItem('segmentedControlPage', window.location.pathname);
};

// Remove Update modal states

export const UpdateOpenModalState = (arr, type) => {
  // const newArr = arr.filter((val) => val?._id !== currentObj?._id);

  const stillHasSameType = arr.some((val) => val?.deleteType === type);

  if (!stillHasSameType) {
    store.dispatch(
      updateOpenModals({
        name: type,
        isOpen: false,
      })
    );
  }
};

export function getVariantLabel(value) {
  const variantLabels = {
    100: 'Thin',
    200: 'Extra Light',
    300: 'Light',
    400: 'Regular',
    500: 'Medium',
    600: 'Semi Bold',
    700: 'Bold',
    800: 'Extra Bold',
    900: 'Black',

    '100italic': 'Thin Italic',
    '200italic': 'Extra Light Italic',
    '300italic': 'Light Italic',
    '400italic': 'Regular Italic',
    '500italic': 'Medium Italic',
    '600italic': 'Semi Bold Italic',
    '700italic': 'Bold Italic',
    '800italic': 'Extra Bold Italic',
    '900italic': 'Black Italic',
  };

  return variantLabels[value] || value;
}

// for fetching the google fonts
export const loadGoogleFontsBatch = (fontFamilies = []) => {
  const link = document.createElement('link');
  const familyQuery = fontFamilies
    .map((f) => f.replace(/ /g, '+'))
    .join('&family=');
  link.href = `https://fonts.googleapis.com/css2?family=${familyQuery}&display=swap`;
  link.rel = 'stylesheet';
  document.head.appendChild(link);
};

// export const handleLightClick = (lightType) => {
//   if (lightType == 'Directional Light') {
//     store.dispatch(
//       updateActiveComponent({
//         option: 'Light',
//         type: 'add',
//       })
//     );
//   }
// };

// === By Prasann ===
// Store separate light properties per type (outside function scope)
const lightMemory = {
  'Ambient Light': null,
  'Directional Light': null,
};

// Add this function to clear light memory

export const ensureLightDataInitialized = () => {
  const currentLightData = store.getState().assets_object.light_data;
  if (!currentLightData || !currentLightData.isLight) {
    store.dispatch(
      lightDataUpdate({
        isLight: true,
        light_type: 'Directional Light',
        type: 'DirectionalLight',
        intensity: 1,
        color: '#ffffff',
        shadow: 'Soft Shadow',
        position: { x: 2, y: 5, z: 2 },
      })
    );
  }
};

export const handleLightClick = (lightType) => {
  ensureLightDataInitialized();

  const currentLightData = store.getState().assets_object.light_data;

  const isSameLightType =
    currentLightData?.light_type === lightType && currentLightData?.isLight;

  // Always open the panel
  store.dispatch(
    updateActiveComponent({
      option: 'Light',
      type: 'add',
      // lightType,
    })
  );

  // Do nothing if already selected
  if (isSameLightType) return;

  // Save current light data before switching
  if (currentLightData?.light_type && currentLightData?.isLight) {
    lightMemory[currentLightData.light_type] = { ...currentLightData };
  }

  // Use stored data if it exists
  const restoredData = lightMemory[lightType];

  let updatedLightData = {
    isLight: true,
    light_type: lightType,
    type: getThreeJSLightType(lightType),
  };

  if (restoredData) {
    // Use stored properties
    updatedLightData = {
      ...restoredData,
      isLight: true,
      light_type: lightType,
      type: getThreeJSLightType(lightType),
    };
  } else {
    // No previous data, use defaults based on light type
    switch (lightType) {
      case 'Directional Light':
        updatedLightData = {
          ...updatedLightData,
          intensity: 1,
          color: '#ffffff',
          shadow: 'Soft Shadow',
          position: { x: 2, y: 5, z: 2 },
        };
        break;
      case 'Ambient Light':
        updatedLightData = {
          ...updatedLightData,
          intensity: 0.5,
          color: '#ffffff',
          shadow: 'No Shadow',
        };
        break;
      case 'Point Light':
        updatedLightData = {
          ...updatedLightData,
          intensity: 1,
          color: '#ffffff',
          shadow: 'Soft Shadow',
          position: { x: 0, y: 3, z: 0 },
          distance: 0,
          decay: 2,
        };
        break;
      case 'Spot Light':
        updatedLightData = {
          ...updatedLightData,
          intensity: 1,
          color: '#ffffff',
          shadow: 'Soft Shadow',
          position: { x: 0, y: 3, z: 0 },
          angle: Math.PI / 3,
          penumbra: 0,
          distance: 0,
          decay: 2,
        };
        break;
      case 'Area Light':
        updatedLightData = {
          ...updatedLightData,
          intensity: 10,
          color: '#ffffff',
          shadow: 'Soft Shadow',
          position: { x: 0, y: 3, z: 0 },
          width: 10,
          height: 10,
        };
        break;
    }
  }

  // Dispatch final result
  store.dispatch(lightDataUpdate(updatedLightData));
};

const getThreeJSLightType = (lightType) => {
  const typeMap = {
    'Directional Light': 'DirectionalLight',
    'Ambient Light': 'AmbientLight',
    'Point Light': 'PointLight',
    'Spot Light': 'SpotLight',
    'Area Light': 'RectAreaLight',
  };
  return typeMap[lightType] || 'DirectionalLight';
};

export const UpdateWindowType = (data) => {
  const { type, current_type, scene } = data;

  let updatedArray = [];
  if (type == current_type) {
    return { ...data, updatedArray };
  } else if (
    (type == 'preview' && current_type == 'code') ||
    (current_type == 'preview' && type == 'code')
  ) {
    return { ...data, updatedArray };
  } else if (
    (type == 'preview' || type == 'code') &&
    current_type == 'design'
  ) {
    const gridHelper = scene.getObjectByName('gridHelper');
    const transformControls = scene.getObjectByName('transformControls');
    const directionalLightHelper = scene.getObjectByName(
      'directionalLightHelper'
    );
    const mrCenterCube = scene.getObjectByName('mrCenterCube');
    if (gridHelper) {
      scene.remove(gridHelper);
      updatedArray.push(gridHelper);
    }
    if (transformControls) {
      scene.remove(transformControls);
      updatedArray.push(transformControls);
    }
    if (mrCenterCube) {
      scene.remove(mrCenterCube);
      updatedArray.push(mrCenterCube);
    }
    if (directionalLightHelper) {
      scene.remove(directionalLightHelper);
    }
    return { type, scene, current_type, updatedArray };
  }

  return { type, scene, current_type, updatedArray };
};

// export const updatePreviewDimention = (addData) => {
//   const gridContainer = document.getElementById('gridContainer');
//   if (!gridContainer) return;

//   // === Base Styles ===
//   gridContainer.style.position = 'fixed';
//   gridContainer.style.width = `${addData.width}px`;
//   gridContainer.style.height = `${addData.height}px`;
//   gridContainer.style.left = '50%';
//   gridContainer.style.top = '50%';
//   gridContainer.style.transform = 'translate(-50%, -50%)';
//   gridContainer.style.boxSizing = 'border-box';
//   gridContainer.style.border = '2px solid #888';

//   // === Canvas Resize ===
//   const canvas = gridContainer.querySelector('canvas');
//   if (canvas) {
//     canvas.style.width = '100%';
//     canvas.style.height = '100%';
//     canvas.width = addData.width;
//     canvas.height = addData.height;
//   }

//   // === Add Bezel Handles (if not already added) ===
//   const bezelSides = ['left', 'right', 'top', 'bottom'];
//   bezelSides.forEach((side) => {
//     let bezel = gridContainer.querySelector(`.bezel-${side}`);
//     if (!bezel) {
//       bezel = document.createElement('div');
//       bezel.className = `bezel bezel-${side}`;
//       gridContainer.appendChild(bezel);
//     }
//     Object.assign(bezel.style, getBezelStyles(side));
//   });

//   // === Optional: Add Drag Behavior ===
//   enableBezelDragging(gridContainer);
// };

// function getBezelStyles(side) {
//   const common = {
//     position: 'absolute',
//     background: 'transparent',
//     zIndex: 10,
//     cursor: {
//       left: 'ew-resize',
//       right: 'ew-resize',
//       top: 'ns-resize',
//       bottom: 'ns-resize'
//     }[side],
//   };

//   const sideStyles = {
//     left: { top: 0, left: 0, width: '10px', height: '100%' },
//     right: { top: 0, right: 0, width: '10px', height: '100%' },
//     top: { top: 0, left: 0, height: '10px', width: '100%' },
//     bottom: { bottom: 0, left: 0, height: '10px', width: '100%' }
//   };

//   return { ...common, ...sideStyles[side] };
// }

// function enableBezelDragging(container) {
//   const bezels = container.querySelectorAll('.bezel');
//   let isDragging = false;
//   let startX, startY, startWidth, startHeight;

//   bezels.forEach((bezel) => {
//     bezel.addEventListener('mousedown', (e) => {
//       e.preventDefault();
//       isDragging = true;
//       startX = e.clientX;
//       startY = e.clientY;
//       startWidth = container.offsetWidth;
//       startHeight = container.offsetHeight;

//       const side = bezel.className.split(' ').find(cls => cls.startsWith('bezel-')).replace('bezel-', '');

//       const onMouseMove = (eMove) => {
//         if (!isDragging) return;

//         if (side === 'right') {
//           container.style.width = `${startWidth + (eMove.clientX - startX)}px`;
//         } else if (side === 'left') {
//           const newWidth = startWidth - (eMove.clientX - startX);
//           container.style.width = `${newWidth}px`;
//           container.style.left = `${container.offsetLeft + (eMove.clientX - startX)}px`;
//         } else if (side === 'bottom') {
//           container.style.height = `${startHeight + (eMove.clientY - startY)}px`;
//         } else if (side === 'top') {
//           const newHeight = startHeight - (eMove.clientY - startY);
//           container.style.height = `${newHeight}px`;
//           container.style.top = `${container.offsetTop + (eMove.clientY - startY)}px`;
//         }
//       };

//       const onMouseUp = () => {
//         isDragging = false;
//         document.removeEventListener('mousemove', onMouseMove);
//         document.removeEventListener('mouseup', onMouseUp);
//       };

//       document.addEventListener('mousemove', onMouseMove);
//       document.addEventListener('mouseup', onMouseUp);
//     });
//   });
// }

export const updatePreviewDimention = (addData) => {
  const gridContainer = document.getElementById('gridContainer');
  if (!gridContainer) return;

  gridContainer.style.position = 'fixed';
  gridContainer.style.width = `${addData.width}px`;
  gridContainer.style.height = `${addData.height}px`;
  gridContainer.style.left = '50%';
  gridContainer.style.top = '50%';
  gridContainer.style.transform = `translate(-50%, -50%)`;

  const canvas = gridContainer.querySelector('canvas');
  if (canvas) {
    // Match CSS display size to the frame — avoid stretch vs drawing buffer.
    canvas.style.width = `${addData.width}px`;
    canvas.style.height = `${addData.height}px`;
  }

  // ⬇️ Trigger rerender (you must pass renderer/camera/scene from context or props)
  updateCameraPreview(addData); // already present
};

/** Clear centered preview-frame styles so design mode is not stretched/clipped. */
export const resetDesignViewportLayout = () => {
  const gridContainer = document.getElementById('gridContainer');
  if (!gridContainer) return;

  gridContainer.style.position = '';
  gridContainer.style.width = '';
  gridContainer.style.height = '';
  gridContainer.style.left = '';
  gridContainer.style.top = '';
  gridContainer.style.transform = '';

  const canvas = gridContainer.querySelector('canvas');
  if (canvas) {
    canvas.style.width = '';
    canvas.style.height = '';
  }
};

/** Restore full-window design viewport after leaving framed preview. */
export const syncDesignViewportSize = () => {
  resetDesignViewportLayout();
  const width = window.innerWidth;
  const height = window.innerHeight;
  updateCameraPreview({ width, height });
};

function resolveSceneLightFromCopy(copiedObject, scene) {
  if (!copiedObject || !scene) return null;

  if (
    copiedObject.type === 'DirectionalLightHelper' ||
    copiedObject.type === 'PointLightHelper' ||
    copiedObject.type === 'SpotLightHelper' ||
    copiedObject.name?.endsWith('-helper')
  ) {
    const lightId = copiedObject.name?.replace(/-helper$/, '');
    return (lightId && scene.getObjectByName(lightId)) || null;
  }

  const isSceneLight =
    copiedObject.isLight ||
    copiedObject.type === 'DirectionalLight' ||
    copiedObject.type === 'PointLight' ||
    copiedObject.type === 'SpotLight' ||
    copiedObject.type === 'AmbientLight' ||
    copiedObject.type === 'RectAreaLight';

  return isSceneLight ? copiedObject : null;
}

function duplicateLightFromCopy(copiedObject, objectt) {
  const scene = objectt?.scene;
  const sceneLight = resolveSceneLightFromCopy(copiedObject, scene);
  if (!sceneLight) return false;

  const { assets_data, light_data = [] } = store.getState().assets_object || {};
  const particles = store.getState().particles?.particles || [];

  let item = findItem(sceneLight, { light_data, assets_data, particles });
  if (!item?.data) {
    const reduxLight = light_data.find(
      (light) =>
        light.id === sceneLight.name ||
        light._id === sceneLight.name ||
        light.name === sceneLight.name
    );
    if (!reduxLight) return false;
    item = { type: 'light', data: { ...reduxLight } };
  }

  const paste = pasteItem({
    dispatch: store.dispatch,
    scene,
    selectObjectInScene,
    setSelectedObjectState: objectt?.setSelectedObjectState,
    setAdditionalCameras: () => {},
    objectt,
  });

  paste({
    type: 'light',
    data: {
      ...item.data,
      position: {
        x: sceneLight.position.x,
        y: sceneLight.position.y,
        z: sceneLight.position.z,
      },
      rotation: {
        x: sceneLight.rotation.x,
        y: sceneLight.rotation.y,
        z: sceneLight.rotation.z,
      },
      scale: {
        x: sceneLight.scale.x,
        y: sceneLight.scale.y,
        z: sceneLight.scale.z,
      },
    },
  });

  return true;
}

export const handlePasteCopy = (objectt) => {
  const state = store.getState();
  const { copied_object } = state.global_state;
  const { assets_data } = store.getState().assets_object;
  const { particles } = store.getState().particles || { particles: [] };

  if (!copied_object) return;

  if (resolveSceneLightFromCopy(copied_object, objectt?.scene)) {
    if (!duplicateLightFromCopy(copied_object, objectt)) {
      toast.error('Could not duplicate light.');
    }
    return;
  }

  if (copied_object?.deleteType === 'image') {
    const itemData = assets_data[copied_object?.deleteType]?.find(
      (v) => v?._id == copied_object?.url
    );
    duplicateImageAsset(itemData, objectt, undefined, copied_object);
  } else if (copied_object?.deleteType === 'mesh') {
    const itemData =
      assets_data.mesh?.find(
        (v) =>
          v?._id === copied_object?.url ||
          v?._id === copied_object?.name ||
          v?.url === copied_object?.url ||
          v?.name === copied_object?.name
      ) ||
      findMeshItemForSceneObject(copied_object) || {
        _id: copied_object.name,
        name: copied_object.file_name || copied_object.name,
        url: copied_object.url || copied_object.name,
      };
    duplicateMeshAsset(itemData, objectt, undefined, copied_object);
  } else if (copied_object?.deleteType === 'video') {
    const itemData = assets_data[copied_object?.deleteType]?.find(
      (v) => v?._id == copied_object?.url
    );
    duplicateVideoAsset(itemData, objectt, undefined, copied_object);
  } else if (copied_object?.deleteType === 'audio') {
    const itemData = assets_data[copied_object?.deleteType]?.find(
      (v) => v?._id == copied_object?.url
    );
    duplicateAudioAsset(itemData, objectt, undefined, copied_object);
  } else if (copied_object?.deleteType === 'text') {
    handleAddText(objectt, copied_object, 'duplicate');
  } else if (copied_object?.deleteType === 'hotspot') {
    const itemData = assets_data[copied_object?.deleteType]?.find(
      (v) =>
        v?._id == copied_object?.url ||
        v?._id == copied_object?.name ||
        v?.name == copied_object?.name
    );
    if (itemData) {
      duplicateHotspot(itemData, objectt);
      toast.success('Hotspot duplicated successfully!');
    }
  } else if (
    copied_object?.deleteType === 'particle' ||
    copied_object?.objType
  ) {
    // Particles are stored in the particles array, not assets_data
    // Try multiple matching strategies
    let particleData = null;

    // Strategy 1: Match by id/name/url from copied_object
    if (copied_object?.url) {
      particleData = particles.find(
        (p) =>
          p.id === copied_object.url ||
          p._id === copied_object.url ||
          p.name === copied_object.url
      );
    }

    // Strategy 2: Match by name property
    if (!particleData && copied_object?.name) {
      particleData = particles.find(
        (p) =>
          p.id === copied_object.name ||
          p._id === copied_object.name ||
          p.name === copied_object.name
      );
    }

    // Strategy 3: Match by userData.id if available
    if (!particleData && copied_object?.userData?.id) {
      particleData = particles.find(
        (p) =>
          p.id === copied_object.userData.id ||
          p._id === copied_object.userData.id
      );
    }

    // Strategy 4: If we found particle data, use it
    if (particleData) {
      duplicateParticleAsset(particleData, objectt);
    } else {
      // Fallback: Try to construct particle data from the copied_object itself
      // This handles cases where the object has particle properties but isn't in Redux yet
      const fallbackParticleData = {
        id: copied_object.name || copied_object.url || `particle_${Date.now()}`,
        name:
          copied_object.name ||
          copied_object.file_name ||
          copied_object.displayName ||
          'particle',
        type: copied_object.objType || copied_object.type || 'fire',
        position: copied_object.position
          ? {
              x: copied_object.position.x,
              y: copied_object.position.y,
              z: copied_object.position.z,
            }
          : { x: 0, y: 0, z: 0 },
        rotation: copied_object.rotation
          ? {
              x: copied_object.rotation.x * (180 / Math.PI), // Convert from radians to degrees if needed
              y: copied_object.rotation.y * (180 / Math.PI),
              z: copied_object.rotation.z * (180 / Math.PI),
            }
          : { x: 0, y: 0, z: 0 },
        scale: copied_object.scale
          ? {
              x: copied_object.scale.x,
              y: copied_object.scale.y,
              z: copied_object.scale.z,
            }
          : { x: 1, y: 1, z: 1 },
        ...(copied_object.userData || {}),
      };

      // Try to find particle in Redux one more time with the constructed id
      const finalParticleData =
        particles.find(
          (p) =>
            p.id === fallbackParticleData.id ||
            p._id === fallbackParticleData.id
        ) || fallbackParticleData;

      duplicateParticleAsset(finalParticleData, objectt);
    }
  }
};

export const FixedValue = (value, upto = 2) => {
  return Number(value || 0).toFixed(upto);
};

export function createLoaderInGridPage(containerId = 'gridPageId') {
  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`No element found with ID: ${containerId}`);
    return null;
  }

  // Create loader wrapper
  const loader = document.createElement('div');
  loader.id = 'dynamic-loader';
  loader.style.position = 'absolute';
  loader.style.top = '0';
  loader.style.left = '0';
  loader.style.width = '100%';
  loader.style.height = '100%';
  loader.style.background = 'rgba(0, 0, 0, 1)';
  loader.style.display = 'flex';
  loader.style.justifyContent = 'center';
  loader.style.alignItems = 'center';
  loader.style.zIndex = '99999999';
  loader.style.transition = 'opacity 0.3s ease';

  // Create spinner or message
  const spinner = document.createElement('div');
  spinner.innerText = 'Loading...'; // You can customize or replace this with a spinner
  spinner.style.color = 'white';
  spinner.style.fontSize = '24px';
  loader.appendChild(spinner);

  // Append only once
  const existing = document.getElementById('dynamic-loader');
  if (!existing) {
    container.appendChild(loader);
  }
  const showLoader = () => {
    loader.style.display = 'flex';
  };
  const hideLoader = () => {
    loader.style.display = 'none';
  };
  const removeLoader = () => {
    loader.remove();
  };

  return {
    showLoader,
    hideLoader,
    removeLoader,
  };
}

// Utility to clear all events from scene, Redux asset arrays, and Redux store
export function clearAllEvents(scene) {
  // 1. Clear all events from scene objects
  if (scene && typeof scene.traverse === 'function') {
    scene.traverse((obj) => {
      if (obj.events) obj.events = [];
      if (obj.userData && obj.userData.events) obj.userData.events = [];
    });
  }
  // 2. Clear all events from Redux asset arrays
  const state = store.getState();
  const assetKeys = [
    'sceneMeshes',
    'sceneHotspots',
    'sceneImages',
    'sceneVideos',
    'sceneAudios',
    'sceneText',
  ];
  assetKeys.forEach((key) => {
    if (state.assets_object[key] && Array.isArray(state.assets_object[key])) {
      state.assets_object[key].forEach((obj) => {
        if (obj.events) obj.events = [];
        if (obj.userData && obj.userData.events) obj.userData.events = [];
      });
    }
  });
  // 3. Clear all events from Redux store
  store.dispatch(require('../redux/Slice/gridSlice').clearAssets(['events']));
}
